import { useAtomCallback } from 'jotai/utils';
import qs from 'qs';
import { useCallback } from 'react';
import { useParams } from 'react-router-dom';

import { getAppConfig } from '../../config';
import { TrackingEvents, sendLoggingEvents } from '../../events';
import { useGetTelehealthSessionInfo } from '../../hooks';
import { useVideoPlatformCallback } from '../../hooks/useVideoPlatform';
import { disarmNavigationGuard } from '../../session/hooks/useNavigationGuard';
import { isRestartingSessionAtom } from '../state';
import { useVisitorDisconnectRoomCallback } from './useVisitorDisconnectRoomCallback';

export function useLeaveSessionCallback() {
  const { providerShortId, patientShortId } = useParams() as {
    providerShortId: string;
    patientShortId: string;
  };
  const { data } = useGetTelehealthSessionInfo(providerShortId, patientShortId);
  const disconnectRoom = useVisitorDisconnectRoomCallback();
  const getIsRestartingSession = useAtomCallback(useCallback((get) => get(isRestartingSessionAtom), []));
  const getVideoPlatform = useVideoPlatformCallback();

  const callback = useCallback(
    function redirectToPostSession() {
      // Check if provider is restarting session
      if (getIsRestartingSession()) {
        return;
      }

      if (!data) return;

      sendLoggingEvents(TrackingEvents.CLIENT_POST_SESSION_REDIRECT);
      const appointmentShortId = data.telehealthSessionInfo?.appointment?.shortId;
      const searchQueryParams = {
        prsid: providerShortId,
        pid: patientShortId,
        utm_source: 'telehealth-rematching',
        video_platform: getVideoPlatform(),
        ...(appointmentShortId && { appointment_id: appointmentShortId }),
      };

      const postSessionLink = `${
        getAppConfig().marketplaceOrigin
      }/session-completed?${qs.stringify(searchQueryParams)}`;

      disarmNavigationGuard();
      disconnectRoom(); // gracefully disconnect from the room
      window.location.href = postSessionLink;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data, providerShortId, patientShortId, getIsRestartingSession],
  );

  return callback;
}
